import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Notification } from '@wdx/clmi/clmi-swagger-gen';
import { WdxToastService } from '@wdx/shared/components/wdx-toast';
import {
    DEFAULT_INPUT_DEBOUNCE_DELAY,
    PAGE_SIZE,
    SignalrEvent,
    TranslationsService,
} from '@wdx/shared/utils';
import { signalrConnected } from 'ngrx-signalr-core';
import { from, of } from 'rxjs';
import {
    buffer,
    catchError,
    debounceTime,
    filter,
    map,
    mergeMap,
    share,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import * as rootReducer from '../../state/_setup/reducers';
import { newSystemSignalrEvent } from '../system-events/system-events.actions';
import * as notificationsActions from './notifications.actions';
import * as notificationsSelectors from './notifications.selectors';
import { NotificationsService } from './notifications.service';

@Injectable()
export class NotificationsEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<rootReducer.State>);
    private notificationsService = inject(NotificationsService);
    private toastService = inject(WdxToastService);
    private translationsService = inject(TranslationsService);

    getPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationsActions.getPage),
            withLatestFrom(
                this.store$.select(
                    notificationsSelectors.getNotificationsPaging,
                ),
            ),
            switchMap(([action, paging]) =>
                this.notificationsService
                    .getPage({
                        pageNumber: action.reset ? 1 : (paging?.page || 0) + 1,
                        pageSize: action.pageSize,
                    })
                    .pipe(
                        map((notifications) =>
                            notificationsActions.getPageSuccess({
                                notifications,
                                reset: action.reset,
                            }),
                        ),
                        catchError((error) =>
                            of(notificationsActions.getPageFailure({ error })),
                        ),
                    ),
            ),
        ),
    );

    getForView$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationsActions.getForView),
            mergeMap(({ filters, paginationOptions }) =>
                this.notificationsService
                    .getForView(filters, paginationOptions)
                    .pipe(
                        map((payload) =>
                            notificationsActions.getForViewSuccess({ payload }),
                        ),
                        catchError((error) =>
                            of(
                                notificationsActions.getForViewFailure({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    listenToEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(newSystemSignalrEvent),
            filter(
                ({ event }) =>
                    event.signalrEvent === SignalrEvent.NotificationsUpdate,
            ),
            mergeMap(({ event }) => [
                notificationsActions.setUnreadNotifications({
                    unreadNotifications: getUnreadNotificationCount(event.data),
                }),
                notificationsActions.refreshNotificationPopups(),
            ]),
        ),
    );

    initialiseNotificationPopups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(signalrConnected),
            map(() => notificationsActions.initialiseNotificationPopups()),
        ),
    );

    loadNotificationPopups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                notificationsActions.initialiseNotificationPopups,
                notificationsActions.refreshNotificationPopups,
            ),
            switchMap(() =>
                this.notificationsService
                    .getPage({
                        pageNumber: 1,
                        pageSize: PAGE_SIZE,
                    })
                    .pipe(
                        map((notifications) =>
                            notificationsActions.loadNotificationPopupsSuccess({
                                notifications,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                notificationsActions.loadNotificationPopupsFailure(
                                    {
                                        error,
                                    },
                                ),
                            ),
                        ),
                    ),
            ),
        ),
    );

    displayNotificationPopup$ = createEffect(
        () => {
            const displayedToastIds = new Set<string>();
            const newNotificationPopups$ = this.store$
                .select(notificationsSelectors.getNotificationPopups)
                .pipe(
                    map((notifications) =>
                        getUndisplayedNotificationPopups(
                            notifications,
                            displayedToastIds,
                        ),
                    ),
                    mergeMap((notifications) => from(notifications)),
                    share(),
                );

            return newNotificationPopups$.pipe(
                buffer(
                    newNotificationPopups$.pipe(
                        debounceTime(DEFAULT_INPUT_DEBOUNCE_DELAY),
                    ),
                ),
                filter((notifications) => notifications.length > 0),
                tap((notifications) => {
                    this.showNotificationToast(
                        notifications[0],
                        notifications.length - 1,
                    );
                }),
            );
        },
        { dispatch: false },
    );

    private showNotificationToast(
        notification: Notification,
        additionalNotificationCount = 0,
    ): void {
        const bodyTemplate =
            this.notificationsService.getNotificationToastTemplate();
        const headerTemplate =
            this.notificationsService.getNotificationToastHeaderTemplate();
        const headerText = this.translationsService.translateTokenisedString(
            'NOTIFICATIONS_NEW_HEADER',
        );
        const additionalNotificationsText =
            additionalNotificationCount > 0
                ? this.translationsService.translateTokenisedString(
                      'NOTIFICATIONS_NEW_HEADER_AND_MORE',
                      {
                          notificationCount:
                              additionalNotificationCount.toString(),
                      },
                  )
                : undefined;

        if (bodyTemplate && notification.id) {
            this.toastService.show({
                body: bodyTemplate,
                classname: 'notification-popup-toast no-body-padding',
                header:
                    additionalNotificationsText && headerTemplate
                        ? headerTemplate
                        : headerText,
                autohide: true,
                templateContext: {
                    notification,
                    displayMessageValue: notification?.displayMessage?.value,
                    caseId: notification?.case?.id,
                    headerText,
                    additionalNotificationsText,
                },
            });
        }
    }

    markAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationsActions.markAsRead),
            mergeMap(({ notifications }) =>
                this.notificationsService.markAsRead(notifications).pipe(
                    map(() =>
                        notificationsActions.markAsReadSuccess({
                            notifications,
                        }),
                    ),
                    catchError((error) =>
                        of(notificationsActions.markAsReadFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    markAllAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationsActions.markAllAsRead),
            switchMap(() =>
                this.notificationsService.markAllAsRead().pipe(
                    map(() => notificationsActions.markAllAsReadSuccess()),
                    catchError((error) =>
                        of(
                            notificationsActions.markAllAsReadFailure({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}

function getUnreadNotificationCount(data: unknown): number {
    if (typeof data === 'number') {
        return data;
    }

    if (Array.isArray(data)) {
        return getUnreadNotificationCount(data[0]);
    }

    if (typeof data === 'string') {
        const unreadNotificationCount = Number(data);
        return Number.isFinite(unreadNotificationCount)
            ? unreadNotificationCount
            : 0;
    }

    return 0;
}

function getUndisplayedNotificationPopups(
    notifications: Notification[] | unknown,
    displayedToastIds: Set<string>,
): Notification[] {
    if (!Array.isArray(notifications)) {
        return [];
    }

    return notifications.filter((notification) => {
        if (!notification.id || displayedToastIds.has(notification.id)) {
            return false;
        }

        displayedToastIds.add(notification.id);
        return true;
    });
}
